import React, { useState, useEffect, useCallback } from 'react';
import UnitTitle from '../unitList/UnitTitle';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  ResponderProvided,
  DraggableProvided,
  DroppableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';
import { AusCourseContent, useMoveCourseContentMutation } from '@api/graphql-codegen';

type Props = {
  lessons: AusCourseContent[];
  droppableId: string;
  courseId: string;
  isPrimary?: boolean;
};

function ContentListSidebar({ lessons, droppableId, courseId, isPrimary }: Props) {
  const [localLessons, setLocalLessons] = useState<Array<AusCourseContent>>(lessons);
  const [selectedUnitId, setSelectedUnitId] = useState<string | undefined>();
  const [enabled, setEnabled] = useState(false);
  const [moveCourseContent] = useMoveCourseContentMutation();

  useEffect(() => {
    if (lessons?.length) {
      setLocalLessons(lessons ?? []);
    }
  }, [lessons]);

  const handleSelect = useCallback((id: string) => async () => {
    setSelectedUnitId(id);
  }, []);

  const handleDragEnd = useCallback(async (result: DropResult, provided?: ResponderProvided) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const updatedUnits = [...localLessons];
    const selectedUnit = updatedUnits[result.source.index];
    delete updatedUnits[result.source.index];
    updatedUnits.splice(result.destination!.index > result.source!.index ? result.destination!.index + 1 : result.destination!.index, 0, selectedUnit);
    setLocalLessons(updatedUnits.filter(u => !!u));
    if (selectedUnit) {
      await moveCourseContent({
        variables: { input: {
          id: selectedUnit.id,
          courseId,
          type: selectedUnit.type,
          oldPosition: result.source!.index + 1,
          newPosition: result.destination!.index + 1,
        }}
      });
    }
    
  }, [courseId, localLessons, moveCourseContent]);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));

    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={droppableId} type='droppableModuleItem'>
        {(droppableProvided: DroppableProvided) => (
          <div
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
          >
            {localLessons.length > 0 ? (
              localLessons.map((item: AusCourseContent, index: number) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(
                    draggableProvided: DraggableProvided,
                    snapshot: DraggableStateSnapshot
                  ) => {
                    return (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                      >
                        <UnitTitle draggableProvided={draggableProvided} snapshot={snapshot} key={item.id} name={item.name} selected={selectedUnitId === item.id} onClick={handleSelect(item.id)} />
                      </div>
                    );
                  }}
                </Draggable>
              ))
            ) : (
              <div style={{ color: 'white', height: '1px' }}>{}</div>
            )}
            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export default ContentListSidebar;
